import React, { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectRenderer } from 'react-dropdown-select';
import styled from 'styled-components';
import { CreatableEnterAction, creatableItemRenderer, CreatableSelect, ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { theme, toRem } from 'design';
import { normalizeString } from 'utils/strings';

interface IProps {
  className?: string;
  /**
   * the text used for the create option in the dropdown.
   *
   * to include the search term in the text, use `{search}`.
   *
   * @example `add {search} as a new job title`
   * @default `add {search}`
   */
  createNewLabel?: string;
  dataCy?: string;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange(selectedOption: ICreatableSelectOption<any> | undefined): void;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  onInputChange?: ChangeEventHandler<HTMLInputElement>;
  onInputClick?: MouseEventHandler;
  onInputFocus?: FocusEventHandler<HTMLInputElement>;
  onInputKeypress?: KeyboardEventHandler<HTMLInputElement>;
  filterOptions?: string[];
  options?: ICreatableSelectOption<any>[];
  onCreate?(newOption: string): void;
  valueField?: string;
  selectId?: string;
  tooltipId?: string;
}

const AddNewOption = styled.div`
  &:hover {
    background-color: ${theme.color.neutral[100]};
  }
`;

const NoData = styled.div`
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)};
  color: ${theme.color.neutral[800]};
  cursor: auto;
`;

const DropdownContainer = styled.div`
  display: block;
  overflow: auto;
`;

const OptionsContainer = styled.div`
  display: block;
  text-transform: capitalize;
`;

export const ItemCreatableSelect: React.FC<IProps> = ({
  className = '',
  createNewLabel,
  dataCy,
  defaultValue,
  disabled,
  placeholder,
  onChange,
  onInputBlur,
  onInputChange,
  onInputClick,
  onInputFocus,
  onInputKeypress,
  filterOptions = null,
  options,
  onCreate,
  valueField,
  selectId,
  tooltipId,
}) => {
  const isMounted = useRef(false);
  const [selected, setSelected] = useState<ICreatableSelectOption<any>>(null);
  const [selectorOptions, setSelectorOptions] = useState<ICreatableSelectOption<any>[]>([]);
  const [disableCreationOnEnter, setDisableCreationOnEnter] = useState(false);

  useEffect(() => {
    if (!options) return;

    setSelectorOptions(options);

    if (!isMounted.current && defaultValue) {
      isMounted.current = true;

      const normalizedValue = defaultValue.toLocaleLowerCase();
      const defaultOption = options.find((opt) => (opt.value[valueField] || opt.value).toLocaleLowerCase() === normalizedValue);
      if (defaultOption) {
        setSelected(defaultOption);
      } else {
        const newItem = { label: defaultValue, value: valueField ? { [valueField]: defaultValue } : defaultValue };
        setSelected(newItem);
        setSelectorOptions([...options, newItem]);
      }
    }
  }, [options]);

  const filteredOptions = useMemo(() => selectorOptions.filter((option) => !filterOptions?.some((opt) => opt.toLocaleLowerCase() === (option.value[valueField] || option.value).toLocaleLowerCase())), [selectorOptions, filterOptions]);

  const onEnterPress = useCallback(
    ({ state, props }: SelectRenderer<ICreatableSelectOption<any>> & { inputRef: React.RefObject<HTMLInputElement> }) => {
      if (typeof state.cursor === 'number') {
        return { action: CreatableEnterAction.Select, optionToSelect: state.values[state.cursor] };
      }

      const exactMatch = props.options.find((value) => value.label.toLocaleLowerCase() === state.search.toLocaleLowerCase());

      if (exactMatch) {
        return { action: CreatableEnterAction.Select, optionToSelect: exactMatch };
      }
      if (state.search.trim() && !disableCreationOnEnter) {
        return { action: CreatableEnterAction.Create };
      }

      return { action: CreatableEnterAction.None };
    },
    [disableCreationOnEnter]
  );

  const onItemChange = useCallback(
    (items: ICreatableSelectOption<any>[]) => {
      setSelected(items[0]);
      onChange(items[0]);
    },
    [onChange]
  );

  const onItemCreate = useCallback(
    (newItem: ICreatableSelectOption<any>) => {
      const createdItem = valueField ? { label: newItem.label, value: { [valueField]: newItem.label } } : newItem;
      setSelectorOptions([...options, createdItem]);
      setSelected(createdItem);
      onCreate?.(createdItem.label);
    },
    [options, onCreate]
  );

  const onItemInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!e.target.value) {
        setSelected(undefined);
      }

      onInputChange?.(e);
    },
    [onInputChange]
  );

  const renderDropdown = useCallback(
    ({ props, state, methods }: SelectRenderer<ICreatableSelectOption<any>>) => {
      const normalizedSearch = normalizeString(state.search);
      const regexp = new RegExp(normalizedSearch, 'i');

      // overrides default search function
      const searchResults = props.options.filter((item) => regexp.test(normalizeString(item.label)) && (props.keepSelectedInList || !methods.isSelected(item)));

      const existingOption = searchResults.some((option) => normalizeString(option.label) === normalizedSearch);
      let addNewOptionComp: JSX.Element;
      let noDataOptionComp: JSX.Element;

      const excluded = filterOptions?.some((opt) => normalizeString(opt) === normalizedSearch);
      const search = state.search.trim().replace(/\s+/g, ' ');
      const searchLabel = `"${search}"`;

      /*
       * only include the add new option if the search text
       * does not match Exactly the title of one of the existing
       * options, if the user has entered valid characters into
       * the search AND if it has not been excluded.
       */
      if (!existingOption && search && !excluded) {
        setDisableCreationOnEnter(false);
        const onAddNewClick: MouseEventHandler = (e) => {
          e.preventDefault();
          methods.createNew(search);
        };

        const parsedCreateNewLabel = createNewLabel ? createNewLabel.replaceAll('{search}', () => searchLabel) : `Add ${searchLabel}`;

        addNewOptionComp = (
          <AddNewOption onClick={onAddNewClick} className="react-dropdown-select-dropdown-add-new" data-cy="add-new-option">
            {parsedCreateNewLabel}
          </AddNewOption>
        );
      } else {
        setDisableCreationOnEnter(true);
      }

      const optionsComps = searchResults?.map((item, itemIndex) => creatableItemRenderer({ item, itemIndex, methods, state, props }));

      if (excluded && search) {
        noDataOptionComp = <NoData>{searchLabel} is not allowed.</NoData>;
      } else if (!search && optionsComps.length === 0) {
        noDataOptionComp = <NoData>No data available. Type to create.</NoData>;
      }

      return (
        <DropdownContainer>
          {noDataOptionComp || addNewOptionComp}
          <OptionsContainer>{optionsComps}</OptionsContainer>
        </DropdownContainer>
      );
    },
    [createNewLabel, filterOptions]
  );

  return (
    <CreatableSelect
      className={className}
      dataCy={dataCy}
      disabled={disabled}
      disableCreationOnEnter={disableCreationOnEnter}
      dropdownRenderer={renderDropdown}
      inputProps={{ maxLength: 40, autoComplete: 'off' }}
      onChange={onItemChange}
      onCreate={onItemCreate}
      onEnterPress={onEnterPress}
      onInputBlur={onInputBlur}
      onInputChange={onItemInputChange}
      onInputClick={onInputClick}
      onInputFocus={onInputFocus}
      onInputKeypress={onInputKeypress}
      options={filteredOptions}
      placeholder={placeholder}
      values={[selected]}
      valueField={valueField ? `value.${valueField}` : undefined}
      selectId={selectId}
      data-tooltip-id={tooltipId}
    />
  );
};
